import { Injectable } from "@angular/core";
import { Subject, Observable, of } from "rxjs";
import {
    IPlannedRouteDetail,
    PlannedRouteService,
} from "./planned-route.service";
import { IPlannedRouteOptions } from "../_models/IPlannedRouteOptions";
import { HttpClient } from "@angular/common/http";
import { tap } from "rxjs/operators";
import { UnplannedStopService } from "./unplanned-stop.service";
import { VehicleService } from "@app/services/vehicle.service";
import { DriverService } from "@app/services/driver.service";
import { DriverAssignmentService } from "@app/services/driverAssignment.service";
import { GeofenceService } from "@app/services/geofence.service";
import { VeriDate } from "@app/services/veriDate.service";
import { IGeofence } from "@app/interfaces/IGeofences";
import { RouteStatus } from "../_enums/RouteStatus";

@Injectable({
    providedIn: "root",
})
export class PlannedRouteService2 {
    plannedRoutesSubject = new Subject<IPlannedRouteDetail[]>();
    private plannedRoutes: IPlannedRouteDetail[];

    constructor(
        private readonly http: HttpClient,
        private readonly unplannedStopService: UnplannedStopService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly vehicleService: VehicleService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly geofenceService: GeofenceService,
        private readonly veriDate: VeriDate
    ) { }

    requestPlannedRoutes(start: Date, end: Date, options: IPlannedRouteOptions): Observable<IPlannedRouteDetail[]> {
        return this.http
            .get<IPlannedRouteDetail[]>(
                `/api/tms/plannedroutes?start=${start.toISOString()}&end=${end.toISOString()}`
            )
            .pipe(
                tap(async (routes) => {
                    this.plannedRoutes = await this.getRouteDependencies(start, end, routes, options);

                    this.plannedRoutesSubject.next(this.plannedRoutes);

                    return this.plannedRoutes;
                })
            );
    }

    async getRouteDependencies(start: Date, end: Date, plannedRoutes: IPlannedRouteDetail[], options: IPlannedRouteOptions): Promise<IPlannedRouteDetail[]> {
        let unplannedStops = options.getUnplannedStops ? await this.unplannedStopService.getUnplannedStopsForRoutes(this.veriDate.createMoment(start), this.veriDate.createMoment(end), true) : [];
        let vehicles = options.mapVehicles ? await this.vehicleService.getVehicles() : [];
        let drivers = options.mapDrivers ? await this.driverService.getDrivers() : [];
        let assignments = options.mapVehicles && options.mapDrivers ? await this.driverAssignmentService.getDriverAssignments(true) : [];
        let geofences = options.mapGeofences ? await this.geofenceService.getGeofences() : [];

        const updatedPlannedRoutes: IPlannedRouteDetail[] = [];
        plannedRoutes.forEach(async (route, index) => {
            if (options.startedRoutesOnly) {
                if (route.Status === RouteStatus.Planned || route.Status === RouteStatus.Started) {
                    return;
                }
            }

            const updatedRoute: IPlannedRouteDetail = route;
            updatedPlannedRoutes.push(updatedRoute);

            if (!options.getCancelledVisits) {
                this.plannedRouteService.filterOutCancelledVisits(route);
            }

            if (options.groupPlannedAndUnplannedVisits) {
                this.plannedRouteService.groupPlannedAndUnplannedVisits(updatedRoute, unplannedStops, options);
            }

            if (options.mapVehicles || options.mapDrivers) {
                this.driverAssignmentService.mapDriversToVehicles(drivers, assignments, vehicles);
                this.plannedRouteService.assignVehicleToRoute(updatedRoute, vehicles);
                this.plannedRouteService.assignDriverToRoute(updatedRoute, drivers);
            }

            this.formatRouteForDisplay(updatedRoute, geofences, options);

            if (options.formatPlannedVisitsForDisplay) {
                this.plannedRouteService.formatPlannedVisitsForDisplay(route, geofences);
            }

            if (options.parseRouteRules) {
                this.parseRouteRules(route);
            }

            if (options.groupPlannedAndUnplannedVisits) {
                this.plannedRouteService.groupPlannedAndUnplannedVisits(updatedRoute, unplannedStops, options);
            }
        });

        if (options.filterRoutes) {
            this.plannedRoutes = this.plannedRouteService.filterRoutes(this.plannedRoutes, options);
        }

        return updatedPlannedRoutes;
    }

    formatRouteForDisplay(route: IPlannedRouteDetail, geofences: IGeofence[], options: IPlannedRouteOptions): void {
        route.totalPlannedDistanceKm = 0;
        this.plannedRouteService.setFirstAndLastVisit(route);

        if (options.editableStatus) {
            this.plannedRouteService.setEditableStatus(route);
        }

        if (options.deletableStatus) {
            this.plannedRouteService.setDeletableStatus(route);
        }

        if (options.mapGeofences) {
            this.plannedRouteService.mapGeofenceToVisit(route.firstVisit, geofences);
            this.plannedRouteService.mapGeofenceToVisit(route.lastVisit, geofences);
        }

        if (options.displayLabelForPlannedVisit) {
            this.plannedRouteService.setDisplayLabelForPlannedVisit(route.lastVisit);
            this.plannedRouteService.setDisplayLabelForPlannedVisit(route.firstVisit);
        }

        if (options.routeDisplayName) {
            this.plannedRouteService.setRouteDisplayName(route);
        }

        if (options.routeDuration) {
            this.plannedRouteService.setRouteDisplayDuration(route);
        }

        if (options.routeDisplayPlannedDistance) {
            this.plannedRouteService.setRouteDisplayPlannedDistance(route);
        }

        if (options.routeDisplayStatus) {
            this.plannedRouteService.setRouteDisplayStatus(route);
        }

        if (options.totalPlannedVisits) {
            this.plannedRouteService.setTotalPlannedVisits(route);
        }
    }

    private parseRouteRules(route: IPlannedRouteDetail): void {
        if (!route.TmsRouteRulesJson) {
            return;
        }

        route.TmsRouteRules = JSON.parse(route.TmsRouteRulesJson);
    }

    updatePlannedRouteCache(plannedRoute: IPlannedRouteDetail): void {
        if (!this.plannedRoutes?.length) {
            return;
        }

        const routeIndexToUpdate = this.plannedRoutes.findIndex(
            (route) => route.Id == plannedRoute.Id
        );

        if(routeIndexToUpdate !== -1){
            this.plannedRoutes[routeIndexToUpdate] = plannedRoute;
            this.plannedRoutesSubject.next(this.plannedRoutes);
        }
    }
}
